import React, { useEffect, useState } from "react";
import { MapPin, Phone, Truck, Navigation, X, Clock } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { STORE } from "@/lib/store";

const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  STORE.address + ", " + STORE.city
)}`;

export const LocationPopup = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem("dcs_visit_popup")) {
      const timer = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const close = () => {
    sessionStorage.setItem("dcs_visit_popup", "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={close}
      data-testid="location-popup-overlay">
      <div
        className="relative w-full max-w-md max-h-[90vh] flex flex-col bg-white shadow-2xl overflow-hidden rounded-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="location-popup">
        {/* Always-visible close (pinned to modal, outside the scroll area) */}
        <button
          onClick={close}
          className="absolute top-2.5 right-2.5 z-20 h-9 w-9 flex items-center justify-center bg-brand-red text-white shadow-lg rounded-full hover:bg-[#b81c14] transition-colors"
          data-testid="location-popup-close"
          aria-label="Fermer">
          <X size={20} />
        </button>

        <div className="overflow-y-auto">
          <div className="bg-brand-navy text-white px-6 pt-6 pb-5 text-center">
            <img src="/logo.png" alt="DCS Électro" className="h-9 w-auto mx-auto bg-white p-1 rounded" />
            <h2 className="font-heading text-xl sm:text-2xl font-extrabold mt-3">{t("popup.title")}</h2>
            <p className="text-white/80 text-sm mt-1 pr-8">{t("popup.sub")}</p>
          </div>

          <div className="grid grid-cols-2">
            <div className="relative">
              <img src={STORE.storePhoto} alt="Magasin DCS Électro" className="w-full h-28 sm:h-36 object-cover" data-testid="location-popup-photo" />
              <span className="absolute bottom-1.5 left-1.5 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5">{t("popup.storeLabel")}</span>
            </div>
            <div className="relative">
              <iframe
                title="DCS Électro - Google Maps"
                src={STORE.mapsEmbed}
                className="w-full h-28 sm:h-36 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-popup-map" />
            </div>
          </div>
          <a href={STORE.mapsLink} target="_blank" rel="noopener noreferrer"
            className="block text-center bg-gray-100 text-brand-navy text-xs font-semibold py-2 hover:bg-gray-200 transition-colors"
            data-testid="location-popup-viewmap">
            {t("popup.viewMap")}
          </a>

          <div className="px-6 py-4 space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="text-brand-red shrink-0 mt-0.5" size={19} />
              <div>
                <p className="font-semibold text-gray-900">{STORE.address}</p>
                <p className="text-sm text-gray-500">{STORE.city}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="text-brand-red shrink-0 mt-0.5" size={19} />
              <div>
                <p className="text-sm font-medium text-gray-700">{t("popup.hours")}</p>
                <p className="text-sm text-gray-500">{STORE.hours}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-brand-yellow/15 border border-brand-yellow/40 p-3">
              <Truck className="text-brand-navy shrink-0 mt-0.5" size={19} />
              <div>
                <p className="font-semibold text-brand-navy text-sm">{t("popup.deliveryTitle")}</p>
                <p className="text-sm text-gray-600">{t("popup.deliveryText")}</p>
              </div>
            </div>
          </div>

          <div className="px-6 pb-4 grid grid-cols-2 gap-3">
            <a href={`tel:${STORE.phoneRaw}`}
              className="flex items-center justify-center gap-2 bg-brand-red text-white font-semibold py-3 hover:bg-[#b81c14] transition-colors"
              data-testid="location-popup-call">
              <Phone size={17} /> {t("popup.call")}
            </a>
            <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-brand-navy text-white font-semibold py-3 hover:bg-black transition-colors"
              data-testid="location-popup-directions">
              <Navigation size={17} /> {t("popup.directions")}
            </a>
          </div>

          <button
            onClick={close}
            className="w-full text-center text-sm text-gray-500 hover:text-brand-red pb-4"
            data-testid="location-popup-continue">
            {t("popup.browse")}
          </button>
        </div>
      </div>
    </div>
  );
};
